import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

interface ProspectDetails {
  id: number
  name: string
  coach_email: string
  coach_name: string | null
  sport: string | null
  status: string
  pool_status: string
}

export default function ProspectNominationConfirm() {
  const [params] = useSearchParams()
  const token = useMemo(() => params.get('token')?.trim() || '', [params])
  const prospectId = useMemo(() => params.get('prospect_id')?.trim() || '', [params])
  const [loading, setLoading] = useState(true)
  const [prospect, setProspect] = useState<ProspectDetails | null>(null)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [supportUrl, setSupportUrl] = useState('')
  const [alreadyConfirmed, setAlreadyConfirmed] = useState(false)

  useEffect(() => {
    if (!token || !prospectId) {
      setLoading(false)
      setError('Missing confirmation token. Check your link and try again.')
      return
    }

    let active = true
    setLoading(true)
    fetch(`${CORE_API}/api/v1/nominations/verify-prospect?token=${encodeURIComponent(token)}&prospect_id=${encodeURIComponent(prospectId)}`)
      .then(async (res) => {
        if (!active) return
        if (!res.ok) {
          const err = await res.json().catch(() => ({}))
          throw new Error(err?.error?.message || 'Unable to load nomination')
        }
        const json = await res.json()
        const data = json?.data?.prospect
        if (!active) return
        setProspect(data)
        if (data?.pool_status === 'pool' || data?.pool_status === 'drawn') {
          setAlreadyConfirmed(true)
          setSuccess(true)
        }
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (!active) return
        setError((err as Error)?.message || 'Unable to load nomination')
        setLoading(false)
      })

    return () => { active = false }
  }, [token, prospectId])

  async function handleConfirm() {
    setSubmitting(true)
    setError('')
    try {
      const res = await fetch(`${CORE_API}/api/v1/nominations/confirm-prospect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, prospect_id: prospectId }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message || 'Confirmation failed')
      }
      const json = await res.json()
      setSupportUrl(json?.data?.support_url || '')
      setSuccess(true)
    } catch (err: unknown) {
      setError((err as Error)?.message || 'Confirmation failed')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-shell to-white">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cocoa border-t-transparent" />
      </div>
    )
  }

  if (error && !prospect) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-shell to-white px-4">
        <div className="max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-bold text-brick">Something went wrong</h1>
          <p className="mt-2 text-sm text-cocoa/70">{error}</p>
          <Link to="/" className="mt-4 inline-block text-sm text-cocoa underline">Back to home</Link>
        </div>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-shell to-white px-4 py-12">
      <div className="w-full max-w-lg rounded-3xl bg-white p-8 shadow-xl space-y-6">
        {success ? (
          <>
            <div className="text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">
                🎉
              </div>
              <h1 className="text-2xl font-bold text-cocoa">
                {alreadyConfirmed ? 'Already Confirmed' : 'You\'re in the Pool!'}
              </h1>
              <p className="mt-2 text-sm text-cocoa/70">
                {prospect?.name} is now in the active draw pool. Share the support link to increase your weight for upcoming ceremonies.
              </p>
            </div>
            {supportUrl && (
              <div className="rounded-xl bg-sand/30 p-4 text-center">
                <p className="text-xs font-bold uppercase tracking-wider text-cocoa/50">Support Link</p>
                <a href={supportUrl} className="mt-1 block text-sm font-semibold text-cocoa underline break-all">{supportUrl}</a>
                <button
                  type="button"
                  className="mt-2 rounded-lg bg-cocoa px-4 py-2 text-xs font-semibold text-white"
                  onClick={() => navigator.clipboard.writeText(supportUrl)}
                >
                  Copy Link
                </button>
              </div>
            )}
            <div className="text-center">
              <Link to="/rally" className="text-sm text-cocoa underline">Visit the Rally Board</Link>
            </div>
          </>
        ) : (
          <>
            <div className="text-center">
              <h1 className="text-2xl font-bold text-cocoa">Confirm Nomination</h1>
              <p className="mt-2 text-sm text-cocoa/70">
                {prospect?.coach_name || 'A coach'} has nominated <strong>{prospect?.name}</strong> for Project Tower.
              </p>
            </div>

            <div className="space-y-3 rounded-xl bg-sand/20 p-4">
              <div className="flex justify-between text-sm">
                <span className="text-cocoa/50">Prospect</span>
                <span className="font-semibold text-cocoa">{prospect?.name}</span>
              </div>
              {prospect?.sport && (
                <div className="flex justify-between text-sm">
                  <span className="text-cocoa/50">Sport</span>
                  <span className="font-semibold text-cocoa">{prospect.sport}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-cocoa/50">Coach</span>
                <span className="font-semibold text-cocoa">{prospect?.coach_name || prospect?.coach_email}</span>
              </div>
            </div>

            {error && <p className="text-sm text-brick">{error}</p>}

            <p className="text-xs text-cocoa/60">
              By confirming, profiles will be created for both the prospect and coach.
              The prospect enters the draw pool for upcoming ceremonies.
            </p>

            <button
              type="button"
              onClick={handleConfirm}
              disabled={submitting}
              className="w-full rounded-xl bg-cocoa px-6 py-3 text-sm font-semibold text-white transition hover:bg-cocoa/90 disabled:opacity-50"
            >
              {submitting ? 'Confirming...' : 'Confirm & Enter Pool'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
